import React from "react";
import { useTheme } from "../../context/ThemeContext";

/**
 * The page's design tokens. Every colour on the page is a CSS custom property
 * on `.aa-root`, set here once per mode, so components are written against
 * ROLES (surface, ink, series-1, good) and dark mode is its own selected steps
 * rather than an inversion.
 *
 * Series and status colours follow the validated reference palette (dataviz
 * skill): categorical hues in a fixed order, status colours reserved for
 * state and never reused for a series.
 */
const LIGHT = {
  "--aa-page": "#f6f7f9",
  "--aa-surface": "#ffffff",
  "--aa-surface-2": "#f9fafb",
  "--aa-surface-3": "#f2f4f7",
  "--aa-hover": "#f5f6f8",
  "--aa-border": "#e4e7ec",
  "--aa-border-strong": "#d0d5dd",
  "--aa-ink": "#101828",
  "--aa-ink-2": "#475467",
  "--aa-muted": "#667085",
  "--aa-faint": "#98a2b3",
  "--aa-grid": "#eaecf0",
  "--aa-axis": "#d0d5dd",
  "--aa-accent": "#4f46e5",
  "--aa-accent-ink": "#4338ca",
  "--aa-accent-soft": "#eef2ff",
  "--aa-accent-ring": "rgba(79, 70, 229, 0.25)",
  "--aa-good": "#0ca30c",
  "--aa-good-ink": "#067647",
  "--aa-good-soft": "#ecfdf3",
  "--aa-warn": "#fab219",
  "--aa-warn-ink": "#b54708",
  "--aa-warn-soft": "#fffaeb",
  "--aa-bad": "#d03b3b",
  "--aa-bad-ink": "#b42318",
  "--aa-bad-soft": "#fef3f2",
  "--aa-neutral": "#c3c7cf",
  "--aa-s1": "#2a78d6",
  "--aa-s2": "#eb6834",
  "--aa-s3": "#1baf7a",
  "--aa-s4": "#eda100",
  "--aa-s5": "#e87ba4",
  "--aa-seq-1": "#cde2fb",
  "--aa-seq-2": "#86b6ef",
  "--aa-seq-3": "#3987e5",
  "--aa-seq-4": "#1c5cab",
  "--aa-shadow": "0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)",
  "--aa-shadow-lg": "0 12px 32px -8px rgba(16, 24, 40, 0.18), 0 4px 8px -4px rgba(16, 24, 40, 0.08)",
  "--aa-skeleton": "#eef0f3",
  "--aa-skeleton-hi": "#f7f8fa",
  "--aa-scrim": "rgba(16, 24, 40, 0.38)",
};

const DARK = {
  "--aa-page": "#12121f",
  "--aa-surface": "#1a1a2b",
  "--aa-surface-2": "#1f1f33",
  "--aa-surface-3": "#25253c",
  "--aa-hover": "#232339",
  "--aa-border": "#2c2c45",
  "--aa-border-strong": "#3a3a58",
  "--aa-ink": "#f2f4f7",
  "--aa-ink-2": "#c0c4d0",
  "--aa-muted": "#9097a8",
  "--aa-faint": "#6b7184",
  "--aa-grid": "#2a2a40",
  "--aa-axis": "#3a3a58",
  "--aa-accent": "#8b83f6",
  "--aa-accent-ink": "#a5a0fa",
  "--aa-accent-soft": "rgba(139, 131, 246, 0.14)",
  "--aa-accent-ring": "rgba(139, 131, 246, 0.35)",
  "--aa-good": "#0ca30c",
  "--aa-good-ink": "#47cd89",
  "--aa-good-soft": "rgba(12, 163, 12, 0.14)",
  "--aa-warn": "#fab219",
  "--aa-warn-ink": "#fdb022",
  "--aa-warn-soft": "rgba(250, 178, 25, 0.12)",
  "--aa-bad": "#e05252",
  "--aa-bad-ink": "#f97066",
  "--aa-bad-soft": "rgba(224, 82, 82, 0.14)",
  "--aa-neutral": "#4a4d63",
  "--aa-s1": "#3987e5",
  "--aa-s2": "#d95926",
  "--aa-s3": "#199e70",
  "--aa-s4": "#c98500",
  "--aa-s5": "#d55181",
  "--aa-seq-1": "#1e2c4a",
  "--aa-seq-2": "#184f95",
  "--aa-seq-3": "#2a78d6",
  "--aa-seq-4": "#86b6ef",
  "--aa-shadow": "0 1px 2px rgba(0, 0, 0, 0.3)",
  "--aa-shadow-lg": "0 16px 40px -8px rgba(0, 0, 0, 0.6)",
  "--aa-skeleton": "#232338",
  "--aa-skeleton-hi": "#2b2b44",
  "--aa-scrim": "rgba(0, 0, 0, 0.55)",
};

export const SERIES = ["var(--aa-s1)", "var(--aa-s2)", "var(--aa-s3)", "var(--aa-s4)", "var(--aa-s5)"];

export function useTokens() {
  const { isDarkMode } = useTheme();
  return { dark: Boolean(isDarkMode), vars: isDarkMode ? DARK : LIGHT };
}

/** The page's stylesheet: what inline styles cannot do — hover, focus, animation, scrollbars. */
const CSS = `
.aa-root { font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: var(--aa-ink); background: var(--aa-page); min-height: 100%; font-size: 14px; line-height: 1.45; -webkit-font-smoothing: antialiased; }
.aa-root *, .aa-root *::before, .aa-root *::after { box-sizing: border-box; }
.aa-root button { font: inherit; }
.aa-num { font-variant-numeric: tabular-nums; }
.aa-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.aa-header { position: sticky; top: 0; z-index: 20; background: color-mix(in srgb, var(--aa-page) 88%, transparent); backdrop-filter: saturate(1.4) blur(10px); border-bottom: 1px solid var(--aa-border); }
.aa-container { max-width: 1480px; margin: 0 auto; padding: 0 28px; }
.aa-content { padding-top: 24px; padding-bottom: 48px; }

.aa-btn { display: inline-flex; align-items: center; gap: 8px; height: 34px; padding: 0 12px; border-radius: 8px; border: 1px solid var(--aa-border); background: var(--aa-surface); color: var(--aa-ink); font-size: 13px; font-weight: 500; cursor: pointer; box-shadow: var(--aa-shadow); transition: background .12s, border-color .12s, box-shadow .12s; white-space: nowrap; }
.aa-btn:hover { background: var(--aa-hover); border-color: var(--aa-border-strong); }
.aa-btn:focus-visible, .aa-input:focus, .aa-tab:focus-visible, .aa-row-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--aa-accent-ring); border-color: var(--aa-accent); }
.aa-btn[disabled] { opacity: .45; cursor: default; }
.aa-btn-ghost { background: transparent; border-color: transparent; box-shadow: none; color: var(--aa-ink-2); }
.aa-btn-ghost:hover { background: var(--aa-hover); border-color: transparent; color: var(--aa-ink); }
.aa-btn-icon { width: 34px; padding: 0; justify-content: center; }
.aa-btn-sm { height: 28px; padding: 0 10px; font-size: 12px; }
.aa-btn-primary { background: var(--aa-accent); border-color: var(--aa-accent); color: #fff; }
.aa-btn-primary:hover { background: var(--aa-accent-ink); border-color: var(--aa-accent-ink); }

.aa-input { height: 34px; padding: 0 10px; border-radius: 8px; border: 1px solid var(--aa-border); background: var(--aa-surface); color: var(--aa-ink); font-size: 13px; box-shadow: var(--aa-shadow); transition: border-color .12s, box-shadow .12s; }
.aa-input::placeholder { color: var(--aa-faint); }
.aa-select { appearance: none; padding-right: 30px; cursor: pointer; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23667085' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; }

.aa-seg { display: inline-flex; padding: 3px; gap: 2px; background: var(--aa-surface-3); border: 1px solid var(--aa-border); border-radius: 9px; }
.aa-seg button { height: 26px; padding: 0 12px; border: 0; border-radius: 6px; background: transparent; color: var(--aa-muted); font-size: 12px; font-weight: 500; cursor: pointer; transition: background .12s, color .12s; }
.aa-seg button:hover { color: var(--aa-ink); }
.aa-seg button[aria-pressed="true"] { background: var(--aa-surface); color: var(--aa-ink); box-shadow: var(--aa-shadow); }

.aa-tabs { display: flex; gap: 2px; overflow-x: auto; scrollbar-width: none; margin-bottom: -1px; }
.aa-tabs::-webkit-scrollbar { display: none; }
.aa-tab { display: inline-flex; align-items: center; gap: 7px; height: 42px; padding: 0 12px; border: 0; border-bottom: 2px solid transparent; background: transparent; color: var(--aa-muted); font-size: 13px; font-weight: 500; cursor: pointer; white-space: nowrap; transition: color .12s, border-color .12s; }
.aa-tab:hover { color: var(--aa-ink); }
.aa-tab[aria-selected="true"] { color: var(--aa-ink); border-bottom-color: var(--aa-accent); }
.aa-tab svg { opacity: .7; }
.aa-tab[aria-selected="true"] svg { opacity: 1; color: var(--aa-accent); }
.aa-tab-sep { width: 1px; background: var(--aa-border); margin: 12px 6px; flex: none; }

.aa-card { background: var(--aa-surface); border: 1px solid var(--aa-border); border-radius: 12px; box-shadow: var(--aa-shadow); min-width: 0; }
.aa-card-head { display: flex; align-items: flex-start; gap: 12px; padding: 16px 20px 0; }
.aa-card-title { font-size: 14px; font-weight: 600; color: var(--aa-ink); }
.aa-card-sub { font-size: 12px; color: var(--aa-muted); margin-top: 2px; }
.aa-card-body { padding: 16px 20px 20px; }
.aa-card-flush { padding: 12px 0 0; }

.aa-kpi { position: relative; padding: 16px 18px 14px; display: flex; flex-direction: column; gap: 6px; transition: border-color .12s, box-shadow .12s; }
.aa-kpi[data-click="true"] { cursor: pointer; }
.aa-kpi[data-click="true"]:hover { border-color: var(--aa-border-strong); box-shadow: var(--aa-shadow-lg); }
.aa-kpi-label { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--aa-muted); font-weight: 500; }
.aa-kpi-value { font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.15; color: var(--aa-ink); }
.aa-kpi-foot { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--aa-muted); min-height: 20px; }

.aa-delta { display: inline-flex; align-items: center; gap: 2px; padding: 1px 6px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.aa-delta-good { color: var(--aa-good-ink); background: var(--aa-good-soft); }
.aa-delta-bad { color: var(--aa-bad-ink); background: var(--aa-bad-soft); }
.aa-delta-flat { color: var(--aa-muted); background: var(--aa-surface-3); }

.aa-pill { display: inline-flex; align-items: center; gap: 6px; height: 22px; padding: 0 8px; border-radius: 999px; font-size: 12px; font-weight: 500; white-space: nowrap; border: 1px solid transparent; }
.aa-pill-good { color: var(--aa-good-ink); background: var(--aa-good-soft); }
.aa-pill-bad { color: var(--aa-bad-ink); background: var(--aa-bad-soft); }
.aa-pill-warn { color: var(--aa-warn-ink); background: var(--aa-warn-soft); }
.aa-pill-neutral { color: var(--aa-ink-2); background: var(--aa-surface-3); }
.aa-pill-accent { color: var(--aa-accent-ink); background: var(--aa-accent-soft); }
.aa-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex: none; }

.aa-chip { display: inline-flex; align-items: center; gap: 6px; height: 28px; padding: 0 4px 0 10px; border-radius: 8px; border: 1px solid var(--aa-border); background: var(--aa-surface); font-size: 12px; box-shadow: var(--aa-shadow); color: var(--aa-ink); max-width: 360px; }
.aa-chip-key { color: var(--aa-muted); }
.aa-chip-val { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.aa-chip button { display: inline-flex; width: 20px; height: 20px; align-items: center; justify-content: center; border: 0; border-radius: 5px; background: transparent; color: var(--aa-muted); cursor: pointer; }
.aa-chip button:hover { background: var(--aa-hover); color: var(--aa-ink); }

.aa-link { color: var(--aa-accent-ink); text-decoration: none; cursor: pointer; background: none; border: 0; padding: 0; font: inherit; }
.aa-link:hover { text-decoration: underline; }
.aa-entity { background: none; border: 0; padding: 0; font: inherit; color: inherit; cursor: pointer; text-align: left; border-radius: 4px; }
.aa-entity:hover { color: var(--aa-accent-ink); text-decoration: underline; text-decoration-color: var(--aa-accent-ring); text-underline-offset: 3px; }

.aa-table-wrap { overflow-x: auto; }
.aa-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
.aa-table th { position: sticky; top: 0; background: var(--aa-surface-2); color: var(--aa-muted); font-weight: 500; font-size: 12px; text-align: left; padding: 9px 14px; border-bottom: 1px solid var(--aa-border); border-top: 1px solid var(--aa-border); white-space: nowrap; user-select: none; }
.aa-table th[data-sort] { cursor: pointer; }
.aa-table th[data-sort]:hover { color: var(--aa-ink); }
.aa-table th:first-child, .aa-table td:first-child { padding-left: 20px; }
.aa-table th:last-child, .aa-table td:last-child { padding-right: 20px; }
.aa-table td { padding: 11px 14px; border-bottom: 1px solid var(--aa-border); color: var(--aa-ink); vertical-align: middle; }
.aa-table tbody tr:last-child td { border-bottom: 0; }
.aa-table tbody tr { transition: background .08s; }
.aa-table tbody tr[data-click="true"] { cursor: pointer; }
.aa-table tbody tr[data-click="true"]:hover td { background: var(--aa-hover); }
.aa-table .aa-r { text-align: right; font-variant-numeric: tabular-nums; }
.aa-sub { display: block; font-size: 12px; color: var(--aa-muted); margin-top: 1px; }

.aa-barlist-row { position: relative; display: flex; align-items: center; gap: 12px; height: 32px; border-radius: 6px; }
.aa-barlist-row[data-click="true"] { cursor: pointer; }
.aa-barlist-row[data-click="true"]:hover .aa-barlist-fill { filter: brightness(0.96); }
.aa-barlist-row[data-click="true"]:hover .aa-barlist-label { color: var(--aa-accent-ink); }
.aa-barlist-track { position: relative; flex: 1; height: 100%; min-width: 0; }
.aa-barlist-fill { position: absolute; inset: 0 auto 0 0; border-radius: 6px; transition: width .4s cubic-bezier(.2,.7,.2,1); }
.aa-barlist-label { position: relative; display: flex; align-items: center; height: 100%; padding: 0 10px; font-size: 13px; color: var(--aa-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.aa-barlist-row[data-stacked="true"] { height: 40px; padding: 0 0 0 4px; }
.aa-barlist-row[data-stacked="true"] .aa-barlist-track { display: flex; flex-direction: column; justify-content: center; gap: 5px; }
.aa-barlist-row[data-stacked="true"] .aa-barlist-label { order: 0; height: auto; padding: 0 6px; }
.aa-barlist-row[data-stacked="true"] .aa-barlist-fill { order: 1; position: relative; inset: auto; height: 4px; margin-left: 6px; max-width: calc(100% - 6px); border-radius: 2px; }
.aa-barlist-row[data-selected="true"] { background: var(--aa-accent-soft); box-shadow: inset 3px 0 0 var(--aa-accent); }
.aa-barlist-row[data-selected="true"] .aa-barlist-label { color: var(--aa-accent-ink); font-weight: 500; }
.aa-table tbody tr[data-selected="true"] td { background: var(--aa-accent-soft); }
.aa-table tbody tr[data-selected="true"] td:first-child { box-shadow: inset 3px 0 0 var(--aa-accent); }
.aa-barlist-value { width: 92px; text-align: right; font-size: 13px; font-weight: 500; font-variant-numeric: tabular-nums; }

.aa-skel { background: linear-gradient(90deg, var(--aa-skeleton) 25%, var(--aa-skeleton-hi) 37%, var(--aa-skeleton) 63%); background-size: 400% 100%; animation: aa-shimmer 1.3s ease infinite; border-radius: 6px; }
@keyframes aa-shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
.aa-fade { animation: aa-fade .22s ease both; }
@keyframes aa-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
.aa-loading-bar { position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; overflow: hidden; }
.aa-loading-bar::after { content: ""; position: absolute; inset: 0; width: 30%; background: var(--aa-accent); animation: aa-load 1s ease-in-out infinite; }
@keyframes aa-load { from { transform: translateX(-100%); } to { transform: translateX(380%); } }

.aa-pop { position: absolute; z-index: 40; background: var(--aa-surface); border: 1px solid var(--aa-border); border-radius: 12px; box-shadow: var(--aa-shadow-lg); animation: aa-fade .14s ease both; }
.aa-menu-item { display: flex; align-items: center; gap: 10px; width: 100%; height: 34px; padding: 0 10px; border: 0; border-radius: 7px; background: transparent; color: var(--aa-ink); font-size: 13px; text-align: left; cursor: pointer; }
.aa-menu-item:hover, .aa-menu-item[data-active="true"] { background: var(--aa-hover); }
.aa-menu-item[aria-selected="true"] { font-weight: 600; }

.aa-cal { display: grid; grid-template-columns: repeat(7, 34px); gap: 2px 0; font-size: 12px; }
.aa-cal-dow { height: 26px; display: flex; align-items: center; justify-content: center; color: var(--aa-faint); font-weight: 500; }
.aa-cal-day { height: 34px; border: 0; background: transparent; color: var(--aa-ink); font-size: 12px; cursor: pointer; font-variant-numeric: tabular-nums; }
.aa-cal-day:hover:not([disabled]) span { background: var(--aa-hover); }
.aa-cal-day span { display: inline-flex; width: 30px; height: 30px; align-items: center; justify-content: center; border-radius: 8px; }
.aa-cal-day[data-in="true"] { background: var(--aa-accent-soft); }
.aa-cal-day[data-edge="start"] { border-radius: 8px 0 0 8px; }
.aa-cal-day[data-edge="end"] { border-radius: 0 8px 8px 0; }
.aa-cal-day[data-edge="both"] { border-radius: 8px; }
.aa-cal-day[data-edge] span { background: var(--aa-accent) !important; color: #fff; font-weight: 600; }
.aa-cal-day[data-today="true"] span { box-shadow: inset 0 0 0 1px var(--aa-border-strong); }
.aa-cal-day[disabled] { color: var(--aa-faint); opacity: .45; cursor: default; }

.aa-tip { position: absolute; z-index: 30; pointer-events: none; background: var(--aa-surface); border: 1px solid var(--aa-border); border-radius: 10px; box-shadow: var(--aa-shadow-lg); padding: 10px 12px; font-size: 12px; min-width: 180px; color: var(--aa-ink); }
.aa-tip-row { display: flex; align-items: center; gap: 8px; padding: 2px 0; }
.aa-tip-row b { margin-left: auto; padding-left: 16px; font-variant-numeric: tabular-nums; }
.aa-swatch { width: 10px; height: 10px; border-radius: 3px; flex: none; }

.aa-legend { display: flex; flex-wrap: wrap; gap: 4px 6px; }
.aa-legend button { display: inline-flex; align-items: center; gap: 6px; height: 24px; padding: 0 8px; border: 0; border-radius: 6px; background: transparent; color: var(--aa-ink-2); font-size: 12px; cursor: pointer; }
.aa-legend button:hover { background: var(--aa-hover); }
.aa-legend button[aria-pressed="false"] { color: var(--aa-faint); }
.aa-legend button[aria-pressed="false"] .aa-swatch { opacity: .3; }

.aa-drawer-scrim { position: fixed; inset: 0; background: var(--aa-scrim); z-index: 60; animation: aa-fadein .18s ease both; }
@keyframes aa-fadein { from { opacity: 0; } to { opacity: 1; } }
.aa-drawer { position: fixed; top: 0; right: 0; bottom: 0; width: min(920px, 96vw); background: var(--aa-page); z-index: 61; box-shadow: var(--aa-shadow-lg); display: flex; flex-direction: column; animation: aa-slide .24s cubic-bezier(.2,.8,.2,1) both; }
@keyframes aa-slide { from { transform: translateX(32px); opacity: 0; } to { transform: none; opacity: 1; } }

.aa-grid { display: grid; gap: 16px; }
.aa-grid-kpi { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.aa-grid-kpi.aa-kpi-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.aa-grid-2 { grid-template-columns: repeat(auto-fit, minmax(420px, 1fr)); }
.aa-grid-3 { grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); }
.aa-grid-main { grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr); }
@media (max-width: 1100px) { .aa-grid-kpi, .aa-grid-kpi.aa-kpi-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); } .aa-grid-main { grid-template-columns: 1fr; } .aa-grid-2 { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .aa-container { padding: 0 16px; } .aa-grid-kpi, .aa-grid-kpi.aa-kpi-3 { grid-template-columns: 1fr; } }

.aa-health-row { display: grid; grid-template-columns: 28px minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 12px 20px; border-top: 1px solid var(--aa-border); }
.aa-health-row:first-child { border-top: 0; }
.aa-icon-tile { width: 28px; height: 28px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; flex: none; }

.aa-info { position: relative; display: inline-flex; color: var(--aa-faint); cursor: help; }
.aa-info:hover { color: var(--aa-ink-2); }
.aa-info .aa-info-tip { display: none; position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%); width: 260px; padding: 8px 10px; border-radius: 8px; background: #101828; color: #f2f4f7; font-size: 12px; font-weight: 400; line-height: 1.45; z-index: 50; box-shadow: var(--aa-shadow-lg); text-transform: none; letter-spacing: 0; white-space: normal; }
.aa-info:hover .aa-info-tip { display: block; }

.aa-bubble-user { justify-self: end; max-width: 78%; background: var(--aa-accent); color: #fff; border-radius: 14px 14px 4px 14px; padding: 9px 13px; font-size: 13px; white-space: pre-wrap; }
.aa-bubble-agent { max-width: 86%; background: var(--aa-surface); border: 1px solid var(--aa-border); border-radius: 14px 14px 14px 4px; padding: 10px 14px; font-size: 13px; line-height: 1.55; white-space: pre-wrap; }
`;

export function StyleSheet() {
  return <style>{CSS}</style>;
}
